import React, { useRef, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';

export interface TouchGestureConfig {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  onTap?: () => void;
  onDoubleTap?: () => void;
  onLongPress?: () => void;
  onPinch?: (scale: number) => void;
  threshold?: number; // Minimum distance for swipe (default: 50px)
  timeThreshold?: number; // Maximum time for swipe (default: 300ms)
  longPressDelay?: number; // Delay for long press (default: 500ms)
}

export interface TouchGestureProps extends TouchGestureConfig {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  showSwipeIndicators?: boolean;
}

interface TouchState {
  startX: number;
  startY: number;
  startTime: number;
  lastTap: number;
  longPressTimer?: NodeJS.Timeout;
  initialDistance?: number;
}

const TouchGestures: React.FC<TouchGestureProps> = ({
  children,
  className,
  disabled = false,
  showSwipeIndicators = false,
  threshold = 50,
  timeThreshold = 300,
  longPressDelay = 500,
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  onTap,
  onDoubleTap,
  onLongPress,
  onPinch,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const touchState = useRef<TouchState>({
    startX: 0,
    startY: 0,
    startTime: 0,
    lastTap: 0,
  });
  const [swipeDirection, setSwipeDirection] = React.useState<'left' | 'right' | null>(null);

  const getDistance = (touch1: Touch, touch2: Touch): number => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (disabled) return;

    const touch = e.touches[0];
    const now = Date.now();

    touchState.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: now,
      lastTap: touchState.current.lastTap,
    };

    // Handle multi-touch for pinch gestures
    if (e.touches.length === 2 && onPinch) {
      touchState.current.initialDistance = getDistance(e.touches[0], e.touches[1]);
    }

    // Start long press timer
    if (onLongPress) {
      touchState.current.longPressTimer = setTimeout(() => {
        onLongPress();
      }, longPressDelay);
    }

    setSwipeDirection(null);
  }, [disabled, onLongPress, onPinch, longPressDelay]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (disabled) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchState.current.startX;
    const deltaY = touch.clientY - touchState.current.startY;

    // Cancel long press if finger moves
    if (touchState.current.longPressTimer && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
      clearTimeout(touchState.current.longPressTimer);
      touchState.current.longPressTimer = undefined;
    }

    // Handle pinch gestures
    if (e.touches.length === 2 && onPinch && touchState.current.initialDistance) {
      const currentDistance = getDistance(e.touches[0], e.touches[1]);
      const scale = currentDistance / touchState.current.initialDistance;
      onPinch(scale);
      return;
    }

    // Show swipe indicators
    if (showSwipeIndicators && Math.abs(deltaX) > 20) {
      if (deltaX > 0 && onSwipeRight) {
        setSwipeDirection('right');
      } else if (deltaX < 0 && onSwipeLeft) {
        setSwipeDirection('left');
      }
    }
  }, [disabled, onPinch, onSwipeLeft, onSwipeRight, showSwipeIndicators]);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (disabled) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchState.current.startX;
    const deltaY = touch.clientY - touchState.current.startY;
    const deltaTime = Date.now() - touchState.current.startTime;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Clear long press timer
    if (touchState.current.longPressTimer) {
      clearTimeout(touchState.current.longPressTimer);
      touchState.current.longPressTimer = undefined;
    }

    // Clear swipe indicators
    setSwipeDirection(null);

    // Check for swipe gestures
    if (distance >= threshold && deltaTime <= timeThreshold) {
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      if (absDeltaX > absDeltaY) {
        // Horizontal swipe
        if (deltaX > 0 && onSwipeRight) {
          onSwipeRight();
        } else if (deltaX < 0 && onSwipeLeft) {
          onSwipeLeft();
        }
      } else {
        // Vertical swipe
        if (deltaY > 0 && onSwipeDown) {
          onSwipeDown();
        } else if (deltaY < 0 && onSwipeUp) {
          onSwipeUp();
        }
      }
      return;
    }

    // Check for tap gestures (only if no significant movement)
    if (distance < 10 && deltaTime < 300) {
      const now = Date.now();
      const timeSinceLastTap = now - touchState.current.lastTap;

      if (timeSinceLastTap < 300 && onDoubleTap) {
        // Double tap
        onDoubleTap();
        touchState.current.lastTap = 0; // Reset to prevent triple tap
      } else {
        // Single tap
        touchState.current.lastTap = now;
        // Delay single tap to check for double tap
        setTimeout(() => {
          if (touchState.current.lastTap === now && onTap) {
            onTap();
          }
        }, 300);
      }
    }
  }, [disabled, threshold, timeThreshold, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, onTap, onDoubleTap]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Add touch event listeners
    element.addEventListener('touchstart', handleTouchStart, { passive: false });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: false });

    // Prevent default touch behaviors that might interfere
    const preventDefaultTouch = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault(); // Prevent pinch-to-zoom on multi-touch
      }
    };

    element.addEventListener('touchstart', preventDefaultTouch, { passive: false });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchstart', preventDefaultTouch);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return (
    <div
      ref={elementRef}
      className={cn('relative touch-manipulation', className)}
      style={{ touchAction: 'manipulation' }}
    >
      {children}
      
      {/* Swipe indicators */}
      {showSwipeIndicators && swipeDirection && (
        <>
          {swipeDirection === 'left' && onSwipeLeft && (
            <div className="swipe-indicator swipe-left">
              <span className="text-sm font-medium">Swipe</span>
            </div>
          )}
          {swipeDirection === 'right' && onSwipeRight && (
            <div className="swipe-indicator swipe-right">
              <span className="text-sm font-medium">Swipe</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Hook for managing swipe-to-delete functionality
export const useSwipeToDelete = (onDelete: () => void, threshold = 100) => {
  const [isRevealed, setIsRevealed] = React.useState(false);
  const [swipeX, setSwipeX] = React.useState(0);

  const handleSwipeMove = (deltaX: number) => {
    if (deltaX < -threshold) {
      setIsRevealed(true);
      setSwipeX(-threshold);
    } else if (deltaX < 0) {
      setSwipeX(deltaX);
      setIsRevealed(false);
    } else {
      setSwipeX(0);
      setIsRevealed(false);
    }
  };

  const handleSwipeEnd = () => {
    if (isRevealed) {
      onDelete();
    }
    setSwipeX(0);
    setIsRevealed(false);
  };

  const reset = () => {
    setSwipeX(0);
    setIsRevealed(false);
  };

  return {
    isRevealed,
    swipeX,
    handleSwipeMove,
    handleSwipeEnd,
    reset,
  };
};

// Hook for pull-to-refresh functionality
export const usePullToRefresh = (onRefresh: () => Promise<void>, threshold = 100) => {
  const [isPulling, setIsPulling] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [pullDistance, setPullDistance] = React.useState(0);

  const handlePullMove = (deltaY: number) => {
    if (deltaY > 0 && window.scrollY === 0) {
      setIsPulling(true);
      setPullDistance(Math.min(deltaY, threshold * 1.5));
    }
  };

  const handlePullEnd = async () => {
    if (isPulling && pullDistance >= threshold) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
    setIsPulling(false);
    setPullDistance(0);
  };

  const isActive = isPulling && pullDistance >= threshold;

  return {
    isPulling,
    isRefreshing,
    pullDistance,
    isActive,
    handlePullMove,
    handlePullEnd,
  };
};

export default TouchGestures;